import React from 'react';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { Table } from '@/components/ui/Table';
import { TableActionButton, TableActions } from '@/components/ui';
import { Button } from '@/components/ui';
import { Eye, Play, RotateCw, X } from 'lucide-react';
import { JobDetails } from './JobDetails';
import { useAdminJobs } from '@/hooks/queries';
import { apiFetch } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';

export function JobsList() {
  usePageHeader({ title: t('Jobs'), breadcrumbs: [{ label: t('Admin'), path: '/admin/orgs' }, { label: t('Background Jobs'), path: '/admin/jobs' }] });

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(15);
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [detailJobId, setDetailJobId] = React.useState<string | null>(null);

  const queryFilters = { page, pageSize, ...filters };
  const { data, isLoading: loading, refetch } = useAdminJobs(queryFilters);
  const items = data?.items || [];
  const total = data?.pagination?.total || 0;

  const columns = [
    { key: 'id', title: t('ID') },
    { key: 'type', title: t('Type') },
    { key: 'status', title: t('Status') },
    { key: 'attempt', title: t('Attempt'), align: 'right' as const },
    { key: 'priority', title: t('Priority'), align: 'right' as const },
    { key: 'queue', title: t('Queue') },
    { key: 'organization_id', title: t('Org') },
    { key: 'run_at', title: t('Run at'), render: (v: string) => v ? `${formatDate(v)} ${formatTime(v)}` : '—' },
    { key: 'started_at', title: t('Started'), render: (v: string) => (v ? `${formatDate(v)} ${formatTime(v)}` : '—') },
    { key: 'finished_at', title: t('Finished'), render: (v: string) => (v ? `${formatDate(v)} ${formatTime(v)}` : '—') },
    {
      key: 'actions',
      title: t('Actions'),
      align: 'right' as const,
      render: (_: any, record: any) => (
        <TableActions>
          <TableActionButton icon={Eye} onClick={() => setDetailJobId(record.id)}>
            View
          </TableActionButton>
          {/* B07: Dev-only "Run now" button — triggers immediate execution for queued/failed jobs */}
          {(record.status === 'queued' || record.status === 'failed') && import.meta.env.DEV && (
            <TableActionButton
              icon={Play}
              onClick={async () => {
                await apiFetch(`/api/admin/jobs/${record.id}/run-now`, { method: 'POST' });
                refetch();
              }}
            >
              Run now
            </TableActionButton>
          )}
          {record.status === 'failed' && (
            <TableActionButton
              icon={RotateCw}
              onClick={async () => {
                await apiFetch(`/api/admin/jobs/${record.id}/retry`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resetAttempts: true }) });
                refetch();
              }}
            >
              Retry
            </TableActionButton>
          )}
          {record.status !== 'completed' && record.status !== 'failed' && (
            <TableActionButton
              icon={X}
              onClick={async () => {
                await apiFetch(`/api/admin/jobs/${record.id}/cancel`, { method: 'POST' });
                refetch();
              }}
            >
              Cancel
            </TableActionButton>
          )}
        </TableActions>
      )
    }
  ];

  const filterOptions = [
    { key: 'status', label: t('Status'), options: [
      { label: t('Queued'), value: 'queued' },
      { label: t('Processing'), value: 'processing' },
      { label: t('Completed'), value: 'completed' },
      { label: t('Failed'), value: 'failed' },
    ]},
    { key: 'type', label: t('Type'), options: [
      { label: t('All'), value: '' },
      { label: 'org_profile_research', value: 'org_profile_research' },
      { label: 'attachment_analysis', value: 'attachment_analysis' },
      { label: 'embed_doc', value: 'embed_doc' },
      { label: 'drafts_ttl_cleanup', value: 'drafts_ttl_cleanup' },
    ]},
    { key: 'queue', label: t('Queue'), options: [ { label: t('default'), value: 'default' }, { label: t('system'), value: 'system' } ] },
  ];

  return (
    <PageContainer>
      <Table
        data={items}
        columns={columns}
        loading={loading}
        searchable={false}
        filters={filterOptions}
        serverSide={true}
        onFilter={(f) => { setFilters(f); setPage(1); }}
        onPaginate={(p, ps) => { setPage(p); setPageSize(ps); }}
        totalItems={total}
      />
      {detailJobId && (
        <JobDetails jobId={detailJobId} onClose={() => setDetailJobId(null)} />
      )}
    </PageContainer>
  );
}


